import {
  Button,
  ModalProps,
  Tabs,
  Timeline,
  Form,
  InputNumber,
  message,
  Select,
  Divider,
  Input,
} from 'antd';
import { useTranslate } from '@tolgee/react';
import { useIsYPHH } from './feature';
import { JustRightHeightModal, TimelineWrapper } from 'components/styled/scoped';
import useModal from 'hooks/useModal';
import { useMemo, useState } from 'react';
import { cx, css } from '@emotion/css';
import Empty from 'components/Empty';
import { Formik, useFormikContext } from 'formik';
import { SimpleCard } from 'views/Conversation/Sider/TradeProduct';
import FormField from 'components/FormField';
import {
  ExchangeItemBody,
  ExchangeProduct,
  exchangeItem,
  getExchangeItemsLog,
  tradeList,
} from 'services/api/conversation';
import { useRequest } from 'ahooks';
import dayjs from 'lib/dayjs';
import { toNumber, get, find } from 'lodash-es';

export const ExchangeButton = (props: ExchangeSelfProps) => {
  const { t } = useTranslate();
  const isYPHH = useIsYPHH();
  const { visible, show, hide } = useModal();
  if (!isYPHH) return null;

  return (
    <div className="flex justify-end">
      <Button
        onClick={() => {
          show();
        }}
        className="mr-[-8px] !text-[12px]"
        type="link"
        size="small"
      >
        {t('换货')}
      </Button>
      {visible && <ExchangeModal {...props} visible={visible} onCancel={() => hide()} />}
    </div>
  );
};

type FormikValues = {
  products: Conversation.Trade.Product[];
  trade: Conversation.Trade.TradeWithLogistics;
  formValue: {
    newSkuId?: ExchangeProduct['newSkuId'];
    quantity?: ExchangeProduct['quantity'];
    customNewSkuId: undefined;
  }[];
};
interface ExchangeSelfProps {
  conversation: Conversation.Conversation;
  products: Conversation.Trade.Product[];
  trade: Conversation.Trade.TradeWithLogistics;
  onSuccess?: (newTrade: Conversation.Trade.TradeWithLogistics) => void;
}
interface ExchangeModalProps extends ModalProps, ExchangeSelfProps {}

export const ExchangeModal = (props: ExchangeModalProps) => {
  const { className, products, trade, conversation, onSuccess, ...restProps } = props;
  const { t } = useTranslate();
  const [activeKey, setActiveKey] = useState('1');

  const { loading, runAsync } = useRequest(
    async (params: ExchangeItemBody) => {
      await exchangeItem(params);
      const { storeId } = conversation;
      const { externalBuyerId, externalTradeId } = trade;
      const newRes = await tradeList({
        page_no: 1,
        page_size: 1,
        internal_store_id: storeId,
        external_buyer_id: externalBuyerId,
        external_trade_ids: externalTradeId,
      });
      const newTrade = newRes.tradeList?.[0];
      return {
        ...trade,
        ...newTrade,
      } as Conversation.Trade.TradeWithLogistics;
    },
    {
      manual: true,
      onSuccess(data) {
        message.success(t('已提交'));
        onSuccess?.(data);
        props.onCancel?.(null!);
      },
      onError(err: any) {
        message.error(err?.response?.data?.msg);
      },
    },
  );
  const initialValues = useMemo(() => {
    return {
      products,
      trade,
      formValue: products.map(() => ({
        newSkuId: undefined,
        quantity: undefined,
        customNewSkuId: undefined,
      })),
    };
  }, [products, trade]);

  return (
    <Formik<FormikValues>
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      validate={(values) => {
        const errors: any = {};
        values.formValue.forEach(({ newSkuId, quantity, customNewSkuId }, index) => {
          if (
            ((newSkuId && newSkuId !== '-1') || (newSkuId === '-1' && !!customNewSkuId)) &&
            !quantity
          ) {
            if (errors.formValue === undefined) {
              errors.formValue = [];
              errors.formValue[index] = t('请输入换货的数量');
            } else {
              errors.formValue[index] = t('请输入换货的数量');
            }
          }
        });
        return errors;
      }}
      onSubmit={(values) => {
        const { formValue } = values;
        const { platform, externalShopId, internalTradeId } = trade;
        const exchangeProdList = formValue
          .map(({ newSkuId, quantity, customNewSkuId }, index) => {
            const { subOrderId } = products[index];
            if (newSkuId === '-1' && !customNewSkuId) {
              return undefined;
            }
            if (!!newSkuId && newSkuId !== '-1') {
              return {
                subOrderId: subOrderId!,
                newSkuId: newSkuId!,
                quantity: quantity!,
              };
            }
            if (newSkuId === '-1' && !!customNewSkuId) {
              return {
                subOrderId: subOrderId!,
                newSkuId: customNewSkuId,
                quantity: quantity!,
              };
            }
            return undefined;
          })
          .filter(Boolean) as ExchangeProduct[];
        if (exchangeProdList.length === 0) {
          message.error(t('请填写换货信息'));
          return Promise.reject();
        }
        runAsync({
          platform,
          externalStoreId: externalShopId,
          internalTradeId,
          exchangeProdList: exchangeProdList,
        });
      }}
    >
      {({ submitForm }) => {
        return (
          <JustRightHeightModal
            {...restProps}
            width={780}
            centered
            onOk={() => submitForm()}
            footer={activeKey === '2' ? null : undefined}
            okText={t('feedback.提交')}
            okButtonProps={{ loading }}
            className={cx(
              className,
              css`
                .ant-modal-body {
                  padding: 0 !important;
                  margin: 0 !important;
                  min-height: 320px;
                  .ant-tabs-nav-wrap {
                    padding-left: 24px;
                    height: 56px;
                  }
                }
              `,
            )}
          >
            <Tabs activeKey={activeKey} onChange={(v) => setActiveKey(v)}>
              <Tabs.TabPane key="1" tab={t('换货')}>
                <ProductForm />
              </Tabs.TabPane>
              <Tabs.TabPane key="2" tab={t('换货记录')}>
                <ExchangeRecord trade={trade} />
              </Tabs.TabPane>
            </Tabs>
          </JustRightHeightModal>
        );
      }}
    </Formik>
  );
};

export const ExchangeRecord = (props: { trade: Conversation.Trade.TradeWithLogistics }) => {
  const {
    trade: { internalTradeId },
  } = props;
  const { t } = useTranslate();
  const { data } = useRequest(() => getExchangeItemsLog(internalTradeId));

  if (data?.exchangeItemLogList && data.exchangeItemLogList.length > 0) {
    return (
      <div className="px-[24px]">
        <TimelineWrapper>
          <Timeline>
            {data?.exchangeItemLogList.map((item) => {
              return (
                <Timeline.Item color="green" key={item.operateTimeMs}>
                  <div>
                    <div className="text-[var(--fett-text-primary)]">
                      {t('换货记录日志', {
                        name: item.userName,
                        old: item.exchangeProd.subOrderId,
                        new: item.exchangeProd.newSkuId ?? '-',
                        count: item.exchangeProd.quantity,
                      })}
                    </div>
                    <div className="text-[var(--fett-text-tertiary)] text-[12px]">
                      {dayjs(toNumber(item.operateTimeMs)).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </TimelineWrapper>
      </div>
    );
  }

  return (
    <div className="h-[220px] flex items-center justify-center">
      <Empty description={t('无换货记录')} />
    </div>
  );
};

export const ProductForm = () => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const { products, trade } = values;
  const { t } = useTranslate();

  return (
    <Form
      className={css`
        .ant-row {
          margin-bottom: 6px !important;
        }
      `}
    >
      <div className="px-[24px]">
        {products.map((product, index) => {
          const options = get(product, 'canExchangeInfo', []);
          const selected = find(options, { exchangedSku: values.formValue[index].newSkuId });

          return (
            <div
              className="border border-solid border-[var(--fett-border-default)]"
              key={`${product.itemId}-${product.sku}`}
            >
              <div className="border-0 border-b border-solid border-[var(--fett-border-default)] bg-[var(--fett-erp-card-bg)] p-[10px] pb-0">
                <SimpleCard
                  key={`${product.itemId}-${index}`}
                  product={product}
                  unit={trade.unit}
                  externalShopId={trade.externalShopId}
                  platform={trade.platform}
                  region={trade.region}
                  showReturn={false}
                />
              </div>
              <div className="p-[10px]">
                <div className="flex [&_div]:basis-[44%] gap-[40px] text-[var(--fett-text-secondary)]">
                  <div>
                    <div className="leading-[20px] mb-[4px]">{t('请输入换货后的商品货号')}</div>
                    <div>
                      <FormField name={`formValue.${index}.newSkuId`}>
                        {({ field }) => {
                          const { onChange, value, ...restField } = field;
                          return (
                            <div className="flex">
                              <Select
                                showSearch
                                allowClear
                                placeholder={t('请选择商品货号')}
                                optionFilterProp="children"
                                optionLabelProp="label"
                                dropdownMatchSelectWidth={false}
                                filterOption={(input, option) => {
                                  return (
                                    (option?.label as string)
                                      .toLowerCase()
                                      .indexOf(input.toLowerCase()) >= 0
                                  );
                                }}
                                onChange={(value) => {
                                  onChange(value);
                                  setFieldValue(`formValue.${index}.quantity`, undefined);
                                }}
                                className="flex-grow"
                                value={value}
                                {...restField}
                              >
                                {options.map((option) => {
                                  return (
                                    <Select.Option
                                      disabled={option.exchangedQuantity === 0}
                                      value={option.exchangedSku}
                                      label={option.exchangedSku}
                                      key={option.exchangedSku}
                                    >
                                      <div>
                                        {option.exchangedSku}
                                        <Divider type="vertical" />
                                        <span>{option.outStockFlag ? '缺货' : '不缺货'}</span>
                                        <Divider type="vertical" />
                                        <span>{option.isSale ? '在售' : '不在售'}</span>
                                        <Divider type="vertical" />
                                        <span>可换货{option.exchangedQuantity}</span>
                                      </div>
                                    </Select.Option>
                                  );
                                })}
                                <Select.Option value="-1" label={t('其他')}>
                                  {t('其他')}
                                </Select.Option>
                              </Select>
                              {value === '-1' && (
                                <Input
                                  placeholder={t('请输入换货后的商品货号')}
                                  value={values.formValue[index].customNewSkuId}
                                  onChange={(e) => {
                                    setFieldValue(
                                      `formValue.${index}.customNewSkuId`,
                                      e.target.value,
                                    );
                                  }}
                                ></Input>
                              )}
                            </div>
                          );
                        }}
                      </FormField>
                    </div>
                  </div>
                  <div>
                    <div className="leading-[20px] mb-[4px]">{t('请输入换货的数量')}</div>
                    <div>
                      <FormField name={`formValue.${index}`}>
                        {({ field }) => {
                          const { value, onChange, ...restField } = field;
                          return (
                            <InputNumber
                              disabled={values.formValue[index].newSkuId === undefined}
                              min={0}
                              max={
                                options.find(
                                  (o) => o.exchangedSku === values.formValue[index].newSkuId,
                                )?.exchangedQuantity
                              }
                              placeholder="0"
                              value={field.value?.quantity}
                              onChange={(value) => {
                                field.onChange({
                                  ...field.value,
                                  quantity: value,
                                });
                              }}
                              {...restField}
                            />
                          );
                        }}
                      </FormField>
                    </div>
                  </div>
                </div>
                {selected && (
                  <div className="bg-[var(--fett-erp-card-bg)] py-[12px] px-[19px]">
                    <div className="leading-[20px] mb-[4px] flex items-center">
                      <div className="basis-2/5">
                        {t('状态')}: {selected.outStockFlag ? '缺货' : '不缺货'} /{' '}
                        {selected.isSale ? '在售' : '不在售'}
                      </div>
                      <div>
                        {t('可换货sku的商品成本')}: {trade.unit} {selected.commodityCost}
                      </div>
                    </div>
                    <div className="leading-[20px] flex items-center">
                      <div className="basis-2/5">
                        {t('可换货sku的数量')}: {selected.exchangedQuantity}
                      </div>
                      <div>
                        {t('可换货sku的平均库存成本')}: {trade.unit} {selected.prodUnitCost}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Form>
  );
};
